import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { api } from "../lib/api.js";
import { haptic } from "../lib/telegram.js";
import { useStore } from "../lib/store.js";

interface SourceRow {
  source: string;
  users: number;
  payers: number;
  stars: number;
}

interface Totals {
  users: number;
  users_7d: number;
  matches: number;
  active_subs: number;
  stars: number;
}

const GAME_NAME: Record<string, string> = {
  chess: "Шахматы",
  durak: "Дурак",
  checkers: "Шашки",
  reversi: "Реверси",
  gomoku: "Пять в ряд",
  seabattle: "Морской бой",
};

/** Человеческое имя канала. */
function sourceTitle(source: string): string {
  if (source.startsWith("src:")) return source.slice(4);
  const known: Record<string, string> = {
    direct: "Прямые заходы",
    affiliate: "Партнёрские ссылки",
    invite: "Приглашения в партию",
    unknown: "Неизвестно",
  };
  return known[source] ?? source;
}

export function Stats() {
  const { setScreen } = useStore();
  const [totals, setTotals] = useState<Totals | null>(null);
  const [sources, setSources] = useState<SourceRow[]>([]);
  const [games, setGames] = useState<{ game: string; matches: number }[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .adminStats()
      .then((res) => {
        if (cancelled) return;
        setTotals(res.totals as Totals);
        setSources(res.bySource as SourceRow[]);
        setGames(res.byGame as { game: string; matches: number }[]);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "не загрузилось");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <motion.div
      className="stack"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="row">
        <button
          className="btn"
          style={{ width: "auto", padding: "8px 14px" }}
          onClick={() => {
            haptic.tap();
            setScreen("lobby");
          }}
        >
          ← Назад
        </button>
        <div style={{ fontWeight: 700, fontSize: 18 }}>Статистика</div>
      </div>

      {error && <div className="muted small">{error}</div>}
      {!totals && !error && (
        <div className="center" style={{ padding: 30 }}>
          <div className="spinner" />
        </div>
      )}

      {totals && (
        <>
          <div className="stat-row">
            <div className="stat">
              <b>{totals.users}</b>
              <span className="muted small">игроков</span>
            </div>
            <div className="stat">
              <b style={{ color: "var(--green)" }}>+{totals.users_7d}</b>
              <span className="muted small">за неделю</span>
            </div>
            <div className="stat">
              <b>{totals.matches}</b>
              <span className="muted small">партий</span>
            </div>
            <div className="stat">
              <b style={{ color: "var(--gold)" }}>{totals.stars}</b>
              <span className="muted small">звёзд</span>
            </div>
          </div>

          <div className="card row" style={{ justifyContent: "space-between" }}>
            <span>Активных подписок</span>
            <b style={{ fontSize: 18 }}>{totals.active_subs}</b>
          </div>

          <div className="muted small" style={{ marginTop: 4 }}>
            Каналы привлечения
          </div>
          <div className="stack" style={{ gap: 6 }}>
            {sources.map((row) => {
              // Конверсия в платящих — главная цифра при выборе канала.
              const conv = row.users > 0 ? (row.payers / row.users) * 100 : 0;
              return (
                <div key={row.source} className="src-row">
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: "block", fontWeight: 600 }}>
                      {sourceTitle(row.source)}
                    </span>
                    <span className="muted small">
                      {row.payers} платящих
                      {row.payers > 0 ? ` · ${conv.toFixed(1)}%` : ""}
                      {row.stars > 0 ? ` · ${row.stars} ⭐` : ""}
                    </span>
                  </span>
                  <b style={{ flex: "none", fontSize: 17 }}>{row.users}</b>
                </div>
              );
            })}
          </div>

          <div className="muted small" style={{ marginTop: 4 }}>
            Партии по играм
          </div>
          <div className="stack" style={{ gap: 6 }}>
            {games.map((g) => (
              <div key={g.game} className="src-row">
                <span style={{ flex: 1 }}>{GAME_NAME[g.game] ?? g.game}</span>
                <b style={{ flex: "none" }}>{g.matches}</b>
              </div>
            ))}
          </div>

          <div className="card stack" style={{ gap: 6, marginTop: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Ссылка с меткой</div>
            <div className="muted small">
              Дай каждому блогеру и каждому объявлению свою — тогда в списке выше
              будет видно, кто реально приводит платящих.
            </div>
            <code className="src-code">t.me/pvp_gamesbot/play?startapp=src_ИМЯ</code>
          </div>
        </>
      )}
    </motion.div>
  );
}
